import React from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { DocumentDraftCreateFormProps } from "models/propTypes/documentDraftPropTypes";
import { DocumentFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ChoiceProps, DateInputProps, FileUploaderProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { CommonEnums, FileEnums } from "models/enums";
import { FeatureConstants, FileConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  GroupSection,
  useWintForm,
  WintForm,
  FileUploader,
  VerticalFormField,
  Choice,
  TextAreaInput,
  DateInput,
} from "components/common/wintForms";
import ReceiptSection from "./_fields/receiptSection";
import IncomingInvoiceSection from "./_fields/incomingInvoiceSection";
import SelfInvoiceSection from "./_fields/selfInvoiceSection";
import { StaticInfoMessage } from "components/common";
import { shouldOfferCostForwardingOnCreate } from "components/costForwardingMarking";
import useInvoicePermissions from "components/invoice/useInvoicePemissions";
import { useFeatureService } from "serviceHooks";
import { Grid } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const DocumentCreateForm: React.ForwardRefRenderFunction<FormHandles, DocumentDraftCreateFormProps> = (props, ref) => {
  const {
    isDocumentDraft,
    defaultValues,
    validationSchema,
    onFormValidationChange,
    availableDocumentTypes,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptPaymentMethodActions,
    receiptCategories,
    setIsReceiptLoading,
    onSubmitSuccess,
  } = props;

  const featureService = useFeatureService();

  const [isRouteGenericVoucherToSupplierInvoiceEnabled, setIsRouteGenericVoucherToSupplierInvoiceEnabled] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    featureService.hasFeature(FeatureConstants.RouteGenericVoucherToSupplierInvoice).then((res) => {
      setIsRouteGenericVoucherToSupplierInvoiceEnabled(res);
    });
  }, []);

  const { wintFormProps, connect, values } = useWintForm<DocumentFormTypes.CreateNew>({
    formRef: ref,
    handleSubmit: props.handleSubmit,
    defaultValues: defaultValues,
    validationSchema: validationSchema,
    onFormValidationChange: onFormValidationChange,
    onSubmitSuccess: onSubmitSuccess,
  });

  const receipt = values.type === CommonEnums.DocumentTypes.Receipt;
  const incomingInvoice = values.type === CommonEnums.DocumentTypes.IncomingInvoice;
  const sieFiles = values.type === CommonEnums.DocumentTypes.SIEFiles;
  const genericDocument = values.type === CommonEnums.DocumentTypes.GenericDocument;
  const selfInvoice = values.type === CommonEnums.DocumentTypes.SelfInvoice;
  const otherDocument = values.type === CommonEnums.DocumentTypes.GenericDocument || sieFiles;
  const paymentMethodGroup = receiptPaymentMethods[0]?.group ?? "Alla";
  const { canUseInvoices } = useInvoicePermissions();
  const showCostForwarding = shouldOfferCostForwardingOnCreate(
    receipt && !!props.allowCostForwarding,
    !!canUseInvoices
  );

  return (
    <WintForm {...wintFormProps}>
      {isDocumentDraft ? (
        <Grid container direction="column" gap={2} pt={1}>
          <VerticalFormField<FileUploaderProps>
            Component={FileUploader}
            {...connect("filesToUpload")}
            label={TranslationKeys.Common.Attachments}
            showMainLabel
            fileUploaderType="full"
            noOuterSpacing
          />
          <VerticalFormField<ChoiceProps>
            Component={Choice}
            {...connect("type")}
            label={TranslationKeys.Document.DocumentType}
            showMainLabel
            items={availableDocumentTypes}
            noOuterSpacing
          />
        </Grid>
      ) : (
        <GroupSection header={TranslationKeys.Common.AttachFiles} className={cssExports.noBorder}>
          <VerticalFormField<FileUploaderProps>
            key={sieFiles ? "create-document-form-sie-file-uploader" : "create-document-file-image-and-pdf-uploaded"}
            Component={FileUploader}
            {...connect("filesToUpload")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Attachments)}
            fileUploaderType="full"
            filePreviewElementId={FileConstants.DialogFilePreviewPortalId}
            // For self invoices, only allow one pdf attachment as of now
            allowedTypes={
              sieFiles
                ? [FileEnums.ContentTypes.SIE]
                : selfInvoice
                  ? [FileEnums.ContentTypes.ApplicationPDF]
                  : undefined
            }
            singleSelection={sieFiles || selfInvoice}
          />
          {!props.isClassifiedReceipt && (
            <VerticalFormField<ChoiceProps>
              Component={Choice}
              {...connect("type")}
              label={TranslationKeys.Document.DocumentType}
              showMainLabel
              items={availableDocumentTypes}
            />
          )}
        </GroupSection>
      )}
      <>
        {receipt ? (
          <ReceiptSection
            connect={connect}
            values={values}
            receiptPaymentMethods={
              props.isClassifiedReceipt
                ? [
                    ...receiptPaymentMethods,
                    {
                      id: -1,
                      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Other),
                      group: paymentMethodGroup,
                    },
                  ]
                : receiptPaymentMethods
            }
            topReceiptPaymentMethods={topReceiptPaymentMethods}
            receiptCategories={receiptCategories}
            receiptPaymentMethodActions={receiptPaymentMethodActions}
            setIsReceiptLoading={setIsReceiptLoading}
            isClassifiedReceipt={props.isClassifiedReceipt}
            showCostForwarding={showCostForwarding}
          />
        ) : incomingInvoice ? (
          <IncomingInvoiceSection />
        ) : selfInvoice ? (
          <SelfInvoiceSection />
        ) : otherDocument ? (
          <GroupSection header="" className={cssExports.noTitle}>
            {genericDocument ? (
              <>
                {isRouteGenericVoucherToSupplierInvoiceEnabled && (
                  <Grid container mt={1}>
                    <Grid item>
                      <StaticInfoMessage gutterBottom>
                        {LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.DocumentDraft.GenericDocumentSelectionInfoBannerPart1
                        )}
                        <span className={cssExports.boldContent}>
                          {LocalizationHelperInstance.getFormattedMessage(
                            TranslationKeys.DocumentDraft.GenericDocumentSelectionInfoBannerBoldText
                          )}
                        </span>
                        {LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.DocumentDraft.GenericDocumentSelectionInfoBannerPart2
                        )}
                      </StaticInfoMessage>
                    </Grid>
                  </Grid>
                )}
                <VerticalFormField<DateInputProps>
                  Component={DateInput}
                  {...connect("document.bookingDate")}
                  label={TranslationKeys.Voucher.BookingDate}
                  optional
                />
              </>
            ) : (
              <></>
            )}
            <VerticalFormField<TextInputProps>
              Component={TextAreaInput}
              {...connect("document.subject")}
              label={TranslationKeys.Common.Comment}
              icon={IconDefinitionConstants.WCommentDots}
              optional
            />
          </GroupSection>
        ) : (
          <></>
        )}
      </>
    </WintForm>
  );
};

export default React.forwardRef(DocumentCreateForm);
